/**
 * @fileoverview The class page's Students tab: the program's Students tab,
 * narrowed to this one class.
 *
 * Same rows (name, the organization's annotation underneath, consent and
 * Archived trailing), same help line, and the same Export dropdown, whose
 * roster and attendance cover this class alone. Read from the class's own
 * roster (`/classes/:id/members`), so it works for a personal class as well
 * as a program's, and for a co-instructor whose own roster holds none of the
 * class's students.
 */

import { useEffect, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Card, SectionHeader } from "@/components/studio/primitives";
import ExportMenu, { classExportItems } from "@/components/studio/ExportMenu";
import { StudentRosterCard } from "@/components/ProgramStudentsTab";

export default function ClassStudentsTab({ classId, className }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setFailed(false);
    api
      .get(`/classes/${classId}/members`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [classId]);

  if (failed) {
    return (
      <Card data-testid="class-students-failed">
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load the students in this class.</p>
      </Card>
    );
  }
  if (!data) return <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>;

  const members = [...(data.members || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  return (
    <div data-testid="class-students">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SectionHeader className="mb-0">Students ({members.length})</SectionHeader>
        {members.length > 0 && (
          <ExportMenu
            client={api}
            errorMessage={getErrorMessage}
            testId="class-export"
            items={classExportItems(`/classes/${classId}`, className)}
          />
        )}
      </div>
      {data.annotation_help && (
        <p className="mb-3 max-w-2xl font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="class-students-help">
          {data.annotation_help}
        </p>
      )}
      <div className="mb-6">
        <StudentRosterCard members={members} />
      </div>
    </div>
  );
}
